import { Button, FormLabel, Modal, Radio, RadioList, TextArea } from "@oysterhr/ui"
import { useTranslation } from "react-i18next"
import * as Yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { Controller, useForm } from "react-hook-form"
import { useMemo } from "react"
import { ACH_SKIP_PLAID_VERIFICATION_REASON_KEY } from "../../../../constants"

interface SkipPlaidVerificationModalProps {
  isOpen: boolean
  onClose: () => void
  onFinish: () => void
}

type FormData = {
  reason: string
  customReason?: string
}

enum ReasonOptions {
  NO_LOGIN_DETAILS = "no-login-details",
  NOT_AUTHORIZED = "not-authorized",
  CANNOT_VERIFY = "cannot-verify",
  DONT_KNOW_PLAID = "dont-know-plaid",
  PREFER_NOT_SHARE = "prefer-not-share",
  OTHER = "other",
}

const FORM_ID = "skipPlaidVerificationForm"
export const SkipPlaidVerificationModal = ({
  isOpen,
  onClose,
  onFinish,
}: SkipPlaidVerificationModalProps) => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/AchLandingPage", {
    keyPrefix: "skipPlaidVerificationModal",
  })

  const schema = useMemo(
    () =>
      Yup.object<FormData>({
        reason: Yup.string().required(t("validations.reasonRequired")),
        customReason: Yup.string().when("reason", {
          is: "other",
          then: Yup.string().required(t("validations.customReasonRequired")),
          otherwise: Yup.string().nullable().optional(),
        }),
      }),
    [t],
  )
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      reason: "",
    },
  })
  const showCustomReason = watch("reason") === "other"

  const onSubmit = (data: FormData) => {
    const reason = data.reason === ReasonOptions.OTHER ? (data.customReason as string) : data.reason

    localStorage.setItem(ACH_SKIP_PLAID_VERIFICATION_REASON_KEY, reason)
    onFinish()
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose}>
      <Modal.Header>{t("title")}</Modal.Header>
      <Modal.Content>
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="reason"
            control={control}
            render={({ field, formState: { errors } }) => (
              <RadioList
                error={errors.reason?.message}
                onChange={event => {
                  field.onChange(event.target.value)
                }}
              >
                {Object.values(ReasonOptions).map(option => (
                  <Radio
                    key={option}
                    value={option}
                    checked={field.value === option}
                    label={<FormLabel label={t(`options.${option}`)} />}
                    id={`reason.option.${option}`}
                    className="mb-3"
                  />
                ))}
              </RadioList>
            )}
          />

          {showCustomReason && (
            <div className="space-y-2">
              <Controller
                name="customReason"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    id="customReason"
                    className="min-h-[100px] resize-none"
                    label={<FormLabel htmlFor="customReason" label={t("reason")} />}
                  />
                )}
              />
              {errors.customReason && (
                <div className="mt-2 text-sm text-error-base">{errors.customReason.message}</div>
              )}
            </div>
          )}
        </form>
      </Modal.Content>

      <Modal.Footer>
        <Button type="button" variation="outline" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button type="submit" form={FORM_ID} variation="primary">
          {t("continue")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
